<script lang="ts">
import { defineComponent, h, VNode } from "vue"
import { mapGetters } from "vuex"
import { toast, ToastItem } from "@/utils/toast"

interface DragState {
  startX: number
  currentX: number
  dragging: boolean
  moved: boolean
}

interface DragStateMap {
  [key: number | string]: DragState
}

interface ToastContentMap {
  [key: number | string]: any
}

export default defineComponent({
  name: "ToastContainer",
  data() {
    return {
      toasts: [] as ToastItem[],
      toastContent: {} as ToastContentMap,
      colors: {
        success: "#10b981",
        error: "#ef4444",
        info: "#3b82f6",
        warning: "#f59e0b",
        ghost: "transparent",
      },
      dragState: {} as DragStateMap,
      DRAG_CLOSE_THRESHOLD: 120,
      MOVE_THRESHOLD: 8,
    }
  },
  computed: {
    ...mapGetters("theme", ["isMobile"]),
    isMobile(): boolean {
      return this.$store.getters["theme/isMobile"]
    },
  },
  created() {
    // Initialize the reactive toasts array from the toast utility
    this.toasts = toast.toasts
  },
  methods: {
    removeToast(id: number | string) {
      toast.remove(id)
    },
    renderContent(content: any): VNode | ReturnType<any> {
      if (typeof content === "string") {
        return h("div", content)
      }
      return content()
    },
    startDrag(id: number | string, clientX: number) {
      this.dragState[id] = {
        startX: clientX,
        currentX: 0,
        dragging: true,
        moved: false,
      }
    },
    moveDrag(id: number | string, clientX: number) {
      const state = this.dragState[id]

      if (!state?.dragging) {
        return
      }

      state.currentX = clientX - state.startX

      if (state.currentX < 0) state.currentX = -1

      if (Math.abs(state.currentX) > this.MOVE_THRESHOLD && state.currentX) {
        state.moved = true
      }
    },
    endDrag(id: number | string) {
      const state = this.dragState[id]

      if (!state) {
        return
      }

      const shouldClose =
        Math.abs(state.currentX) > this.DRAG_CLOSE_THRESHOLD ||
        Math.abs(state.currentX) === 0

      if (shouldClose) {
        if (Math.abs(state.currentX) === 0) {
          // Type assertion to access $el which might not be on 'any'
          const contentEl = this.toastContent[id] as { $el?: HTMLElement }
          contentEl?.$el?.click()
        }

        this.removeToast(id)
      }

      setTimeout(() => {
        delete this.dragState[id]
      }, 50)
    },
    getToastStyle(toastItem: ToastItem) {
      const state = this.dragState[toastItem.id]

      const translateX = state?.currentX || 0

      const opacity = 1 - Math.min(Math.abs(translateX) / 240, 0.7)

      return {
        background: this.colors[toastItem.type],
        "--drag-x": `${translateX}px`,
        opacity,
        transition: state?.dragging
          ? "none"
          : "transform .25s ease, opacity .25s ease",
      }
    },
  },
})
</script>

<template>
  <div class="toast-container" :class="{ mobile: isMobile }">
    <TransitionGroup name="toast">
      <div v-for="toast in toasts" :key="toast.id" class="toast-wrapper">
        <div
          class="toast"
          :class="[toast.type, { dragging: dragState[toast.id]?.dragging }]"
          :style="getToastStyle(toast)"
          @touchstart="startDrag(toast.id, $event.touches[0].clientX)"
          @touchmove="moveDrag(toast.id, $event.touches[0].clientX)"
          @touchend="endDrag(toast.id)"
          @mousedown.prevent.stop="startDrag(toast.id, $event.clientX)"
          @mousemove.prevent.stop="moveDrag(toast.id, $event.clientX)"
          @mouseup.prevent.stop="endDrag(toast.id)"
          @mouseleave.prevent.stop="endDrag(toast.id)"
        >
          <component
            class="toast-notification-component"
            :is="renderContent(toast.content)"
            :ref="(el: HTMLElement) => { if (el) toastContent[toast.id] = el }"
          />
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.toast-container {
  max-height: calc(100dvh - 100px);
  overflow-x: hidden;
  overflow-y: auto;
  position: fixed;
  top: 64px;
  right: 20px;
  z-index: 10000;

  display: flex;
  flex-direction: column;
  gap: 10px;
}

.toast-container.mobile {
  right: 0;
}

.toast-wrapper {
  transition: transform 0.3s ease;
}

.toast {
  color: white;

  padding: 12px 20px;

  border-radius: 8px;

  min-width: max-content;
  max-width: 400px;

  box-shadow: 0 4px 0.75rem rgba(0, 0, 0, 0.15);

  font-size: 14px;
  font-weight: 500;

  pointer-events: auto;

  touch-action: pan-y;
  user-select: none;

  cursor: grab;

  transform: translateX(var(--drag-x, 0px));
}

.toast:active {
  cursor: grabbing;
}

.toast-container.mobile .toast {
  max-width: unset;
}

.toast-container.mobile .toast.ghost {
  min-width: 100dvw;
}

.toast.ghost {
  padding: 0;
  color: unset;
}

/* ENTER */

.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(400px);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(400px);
}

.toast-move {
  transition: transform 0.3s ease;
}

.dragging .toast-notification-component {
  pointer-events: none;
}
</style>
